/**
 * PATH: packages/ui/src/components/email/recipients.ts
 * PURPOSE:
 *   - Universal helpers for rendering an email's To/Cc/Bcc lists.
 *
 * WHY THIS EXISTS:
 *   - Mailbox upstreams (Microsoft Graph, Gmail, IMAP parsers) all
 *     carry recipients as `{ name, email }` objects so the UI can
 *     show a human-friendly display name. Quotech's backend stores
 *     the JSON payload as-is in the `to_addresses` JSONField column.
 *   - Pre-this-MR, the @quotech/ui `EmailMessage` type declared
 *     `to_addresses?: string[]` and host apps rendered it via
 *     `email.to_addresses?.join(', ')`. JS happily coerces an
 *     object via the default `toString()` → `'[object Object]'`,
 *     so the UI showed `To: [object Object], [object Object], ...`
 *     for every email synced from Outlook (the reported bug).
 *   - Centralising the formatting here means future format changes
 *     (e.g. clickable mailto chips, "+ N more" overflow handling)
 *     happen in one place instead of being duplicated across 3+
 *     pages in 2+ tenant apps.
 *
 * NOTES FOR FUTURE AI:
 *   - Keep these helpers pure + dependency-free (no React, no
 *     lucide-react). They run inside server-rendered chunks and
 *     non-React contexts (e.g. embedding builders).
 */

import type { EmailRecipient } from './types'

/** Coerce one recipient — either bare string or `{name, email}`
 *  object — to the canonical RFC-5322 mailbox address. Returns
 *  empty string when neither shape carries an address (defensive;
 *  some upstream payloads include `null` / `undefined` entries). */
export function getRecipientEmail(r: EmailRecipient | null | undefined): string {
  if (!r) return ''
  if (typeof r === 'string') return r
  return r.email || r.address || ''
}

/** Pick the most user-friendly label for a single recipient: the
 *  display name when present, otherwise the email address. Empty
 *  string when neither is set. */
export function formatRecipientName(r: EmailRecipient | null | undefined): string {
  if (!r) return ''
  if (typeof r === 'string') return r
  if (r.name && r.name.trim()) return r.name.trim()
  return r.email || r.address || ''
}

/** Format an entire To/Cc/Bcc list as a single comma-separated
 *  string suitable for inline display in an email row or header.
 *
 *  Examples:
 *    formatRecipientList([{name:'Jake', email:'jake@x.com'}, 'a@b.com'])
 *      → 'Jake, a@b.com'
 *    formatRecipientList(undefined) → ''
 *
 *  When `max` is provided and the list exceeds it, the result is
 *  truncated with a trailing `+ N more` segment so the UI can
 *  surface long Cc lists without wrapping endlessly. */
export function formatRecipientList(
  recipients: EmailRecipient[] | null | undefined,
  options?: { max?: number; separator?: string },
): string {
  if (!recipients || recipients.length === 0) return ''
  const sep = options?.separator ?? ', '
  const max = options?.max
  const names = recipients.map(formatRecipientName).filter((n) => n.length > 0)
  if (names.length === 0) return ''
  if (max && names.length > max) {
    const head = names.slice(0, max).join(sep)
    const extra = names.length - max
    return `${head}${sep}+ ${extra} more`
  }
  return names.join(sep)
}
